import {useDispatch} from "react-redux";
import {useAppSelector} from "../../store/hooks/useAppSelector";
import {getAppDttm} from "../../store/ui/selectors";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {setAppDate} from "../../store/ui/actions";
import {ShiftToolbar} from "../../components/ShiftToolbar/ShiftToolbar";
import {
  addDays,
  adjustToTimeZone,
  endOfDay,
  getObjectId,
  ShiftEntity,
  shiftsSelector,
  startOfDay,
  subDays,
  useSearchShiftsQuery,
  useShiftPrefetch
} from "@ots-frontend/ots-data";
import {Card, Container, Group, LoadingOverlay, Menu, rem, Stack, Text, Title, UnstyledButton} from "@mantine/core";
import {
  GridSort,
  HasAccess,
  ResizeableContent, ShiftDataGrid,
  ShiftDataGridContextMenuItem,
  ShiftFilterFormModal,
  ShiftFilterFormValues,
  ThemedPage,
  useApp
} from "@ots-frontend/ots-ui";
import {IconChevronDown} from "@tabler/icons-react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {getShiftPageFilter, getShiftPageState} from "../../store/shift-page/selectors";
import {CompactSelection, DataEditorRef, GridSelection, Item} from "@glideapps/glide-data-grid";
import {useDisclosure} from "@mantine/hooks";
import {CellClickedEventArgs} from "@glideapps/glide-data-grid/dist/ts/data-grid/data-grid-types";
import {
  setShiftPageFilter,
  setShiftPageStateCell,
  setShiftPageStateSort,
  setShiftPageStateWidths
} from "../../store/shift-page/actions";

const sortShifts = (a: ShiftEntity, b: ShiftEntity): number => {
  if (typeof a.endDttm === "undefined" && typeof b.endDttm === "undefined") {
    return a.startDttm - b.startDttm;
  } else if (typeof a.endDttm === "undefined") {
    return -1;
  } else {
    return 1;
  }
};

export const ShiftPage = () => {

  const appDttm = useAppSelector(getAppDttm);
  const filter = useAppSelector(getShiftPageFilter)
  const gridState = useAppSelector(getShiftPageState)

  const dispatch = useDispatch();
  const {regionId} = useApp();
  const navigate = useNavigate()
  const {t} = useTranslation(["translation", "common"])

  const dataEditorRef = useRef<DataEditorRef>(null);

  const [filterOpened, filterHandler] = useDisclosure(false);
  const [scrollTimerReference, setScrollTimerReference] = useState<NodeJS.Timeout | undefined>(undefined);
  const [showSearch, setShowSearch] = useState(false);
  const onSearchClose = useCallback(() => setShowSearch(false), []);

  const [selection, setSelection] = useState<GridSelection>({
    columns: CompactSelection.empty(),
    rows: CompactSelection.empty()
  });

  const [colWidths, setColWidths] = useState<(number | undefined)[]>(gridState.widths)
  const [sort, setSort] = useState<GridSort | undefined>(gridState.sort)

  const prefetchShifts = useShiftPrefetch("searchShifts");

  const selectShifts = useMemo(() => {
    return shiftsSelector()
  }, []);

  const {shifts, isFetching, refetch} = useSearchShiftsQuery({
    fromDttm: adjustToTimeZone(startOfDay(appDttm)).getTime(),
    toDttm: adjustToTimeZone(endOfDay(appDttm)).getTime(),
    regionIds: regionId ? [regionId] : undefined,
  }, {
    pollingInterval: 120000,
    selectFromResult: (result) => ({
      ...result,
      shifts: selectShifts(result, {
        personId: getObjectId(filter.person),
        vehicleId: getObjectId(filter.vehicle),
        trailerId: getObjectId(filter.trailer),
        dateSort: true,
      }),
      isFetching: result.isFetching,
    })
  });

  const prefetchDttmRange = useCallback((date: Date) => {
    const rootFromDate = startOfDay(date);
    const rootToDate = endOfDay(date);

    // prefetch the previous day
    if (regionId) {
      prefetchShifts({
        fromDttm: adjustToTimeZone(subDays(rootFromDate, 1)).getTime(),
        toDttm: adjustToTimeZone(subDays(rootToDate, 1)).getTime(),
        regionIds: [regionId],
      });

      // prefetch the next day
      prefetchShifts({
        fromDttm: adjustToTimeZone(addDays(rootFromDate, 1)).getTime(),
        toDttm: adjustToTimeZone(addDays(rootToDate, 1)).getTime(),
        regionIds: [regionId],
      });
    }

  }, [prefetchShifts, regionId]);

  const onDateChange = useCallback((date: Date) => {
    dispatch(setAppDate(date.getTime()));
    prefetchDttmRange(date);
  }, [dispatch, prefetchDttmRange]);

  const onItemClick = useCallback((item: ShiftDataGridContextMenuItem, data: ShiftEntity) => {
    if (item === "NEW") {
      navigate("form", {state: {id: data.id, copy: true}});
    } else if (item === "EDIT") {
      navigate("form", {state: {id: data.id}});
    }
  }, [navigate]);

  useEffect(() => {
    setScrollTimerReference(
      setTimeout(() => {
        if (gridState?.cell) {
          dataEditorRef?.current?.scrollTo(0, gridState.cell[1], "both", 0, 0, {
            vAlign: "start",
            hAlign: "start"
          });
          setSelection(prevState => gridState.cell ? ({
            ...prevState,
            current: {
              cell: [0, gridState.cell[1]],
              range: {
                x: 0,
                y: gridState.cell[1],
                width: 1,
                height: 1
              },
              rangeStack: []
            }
          }) : ({...prevState}));
        }
      }, 200)
    );

    return () => {
      clearTimeout(scrollTimerReference)
    }
  }, []);

  const onFilterSubmit = useCallback((filter: ShiftFilterFormValues) => {
    dispatch(setShiftPageFilter(filter))
    filterHandler.close()
  }, [dispatch, filterHandler])

  const onCellClicked = useCallback((cell: Item, event: CellClickedEventArgs) => {
    dispatch(setShiftPageStateCell([cell[0], cell[1]]))
  }, [dispatch])

  useEffect(() => {
    return () => {
      dispatch(setShiftPageStateSort(sort))
    }
  }, [sort, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(setShiftPageStateWidths(colWidths))
    }
  }, [colWidths, sort, dispatch]);

  return (
    <ThemedPage>
      {(classes) => (
        <ResizeableContent>
          {(size) => (
            <Container h={size.height} w={size.width} className={classes.container} fluid={true} p="md">
              <Stack h="100%" m="sm">
                <Card shadow="sm" mb="lg" radius="md" withBorder>
                  <Group position="apart">
                    <Title
                      order={4}>{t("page.shifts.title", "Shifts", {ns: "translation"})}</Title>
                    <Menu
                      width={260}
                      position="bottom-end"
                      transitionProps={{transition: "pop-top-right"}}
                      withinPortal
                    >
                      <Menu.Target>
                        <UnstyledButton>
                          <Group spacing={7}>
                            <Text weight={500} size="sm" sx={{lineHeight: 1}} mr={3}>
                              {t("common.action.plural", "Actions", {ns: "common"})}
                            </Text>
                            <IconChevronDown size={rem(12)} stroke={1.5}/>
                          </Group>
                        </UnstyledButton>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item onClick={() => refetch()}>
                          {t("common.action.refresh", "Refresh", {ns: "common"})}
                        </Menu.Item>
                        <Menu.Divider/>
                        <HasAccess permissions={["edit"]}>
                          <Menu.Item onClick={() => navigate("form")}>
                            {t("common.action.new.shift", "New Shift", {ns: "common"})}
                          </Menu.Item>
                        </HasAccess>
                      </Menu.Dropdown>
                    </Menu>
                  </Group>
                  <ShiftToolbar
                    date={new Date(appDttm)}
                    onDateChange={onDateChange}
                    showSearch={showSearch}
                    onSearchClick={() => setShowSearch(!showSearch)}
                    filter={filter}
                    onFilterClick={() => filterHandler.open()}/>
                </Card>

                <LoadingOverlay visible={isFetching} overlayBlur={2}/>
                <ShiftDataGrid
                  ref={dataEditorRef}
                  height="100%"
                  showSearch={showSearch}
                  onSearchClose={onSearchClose}
                  onCellClicked={onCellClicked}
                  gridSelection={selection}
                  onGridSelectionChange={setSelection}
                  colWidths={gridState.widths}
                  onColumnResize={setColWidths}
                  sort={gridState.sort}
                  onSort={setSort}
                  data={shifts}
                  onItemClick={onItemClick}
                />
              </Stack>

              <ShiftFilterFormModal
                title={t<string>("common.action.filter", "Filter", {ns: "common"})}
                opened={filterOpened}
                onClose={filterHandler.close}
                onSubmit={onFilterSubmit}
                defaultValues={{
                  ...filter
                }}
              />

            </Container>
          )}
        </ResizeableContent>
      )}
    </ThemedPage>
  );
};
